import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionService } from '../services/subscription.service';
import type { Plan } from '../services/subscription.service';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { SectionHeading } from '../components/shared/SectionHeading';
import { PassCheckoutDialog } from '../components/payments/PassCheckoutDialog';
import { usePassCheckout } from '../hooks/usePassCheckout';
import { formatCurrency } from '../utils/currency';
import { getPlanLabel } from '../utils/planLabels';
import {
  FREE_TIER_I18N_KEY,
  PASS_DURATION_LABEL_KEYS,
  PASS_TIERS,
  isPassesModel,
} from '../config/pricing';

export const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const showingPasses = isPassesModel;
  const { requestCheckout, isDialogOpen, isSubmitting, error, submitEmail, closeDialog } =
    usePassCheckout();

  useEffect(() => {
    if (showingPasses) {
      setLoading(false);
      return;
    }

    const loadPlans = async () => {
      try {
        const data = await subscriptionService.getPlans();
        setPlans(data);
      } catch (error) {
        console.error('Failed to load plans:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, [showingPasses]);

  const handleSubscribe = async (planId: string) => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/pricing');
      return;
    }

    setSubscribing(planId);
    try {
      const data = await subscriptionService.createCheckout(planId);
      const url = data?.url;
      if (url && typeof url === 'string') {
        // Redirect to Stripe Checkout (or mock dashboard URL when Stripe not configured)
        window.location.href = url;
        return;
      }
      alert('We kregen geen checkout-link terug. Probeer het opnieuw of neem contact op.');
    } catch (error) {
      console.error('Failed to create checkout:', error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      alert(message || 'Het starten van de checkout is mislukt. Probeer het opnieuw.');
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <div className="page-shell">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <Header />
      <section className="section-shell">
        <div className="mx-auto max-w-6xl px-4">
          <SectionHeading
            eyebrow={showingPasses ? t('pricing.passes.pageEyebrow') : t('pricing.pageEyebrow')}
            title={showingPasses ? t('pricing.passes.pageTitle') : t('pricing.pageTitle')}
            subtitle={
              showingPasses ? t('pricing.passes.pageDescription') : t('pricing.pageDescription')
            }
          />

          {showingPasses ? (
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-5">
              <div className="relative flex flex-col overflow-hidden rounded-[30px] border border-primary/10 bg-white/90 p-8 shadow-surface">
                <h2 className="text-2xl font-bold text-ink">{t('pricing.passes.freeLabel')}</h2>
                <div className="mb-6 mt-4">
                  <span className="text-4xl font-bold text-primary">
                    {t('landing.pricing.freePrice')}
                  </span>
                </div>
                <p className="mb-6 text-sm text-slate-600">{t('pricing.passes.freeDescription')}</p>
                <ul className="mb-8 space-y-3">
                  {(t(`${FREE_TIER_I18N_KEY}.features`, { returnObjects: true }) as string[]).map(
                    (feature, index) => (
                      <li key={index} className="flex items-start text-sm text-slate-700">
                        <svg
                          className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    )
                  )}
                </ul>
                <button
                  type="button"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/try-free')}
                  className="mt-auto flex h-14 w-full items-center justify-center whitespace-nowrap rounded-xl bg-slate-100 px-4 font-semibold text-slate-900 transition-all hover:bg-slate-200"
                >
                  {t(`${FREE_TIER_I18N_KEY}.cta`)}
                </button>
              </div>

              {PASS_TIERS.map((tier) => (
                <div
                  key={tier.id}
                  className={`relative flex flex-col overflow-hidden rounded-[30px] border p-8 shadow-surface ${
                    tier.recommended
                      ? 'border-secondary/60 bg-white ring-2 ring-secondary/20'
                      : 'border-primary/10 bg-white/90'
                  }`}
                >
                  {tier.recommended && (
                    <span className="rounded-full bg-secondary px-3 py-1 text-center text-xs font-semibold uppercase tracking-wide text-white">
                      {t('pricing.passes.mostChosen')}
                    </span>
                  )}

                  <h2 className="mt-4 text-2xl font-bold text-ink">{t(`${tier.i18nKey}.name`)}</h2>
                  <p className="mt-1 text-sm font-medium text-secondary">
                    {t(PASS_DURATION_LABEL_KEYS[tier.duration])}
                  </p>
                  <div className="mb-1 mt-4">
                    <span className="text-4xl font-bold text-primary">
                      {formatCurrency(tier.price, i18n.language)}
                    </span>
                    <span className="ml-1 text-sm text-slate-600">{t('pricing.passes.oneTime')}</span>
                  </div>
                  <p className="mb-6 text-xs text-slate-500">{t('pricing.passes.vatIncluded')}</p>

                  <ul className="mb-8 space-y-3">
                    {(t(`${tier.i18nKey}.features`, { returnObjects: true }) as string[]).map(
                      (feature, index) => (
                        <li key={index} className="flex items-start text-sm text-slate-700">
                          <svg
                            className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>{feature}</span>
                        </li>
                      )
                    )}
                  </ul>

                  <div className="mt-auto">
                    <button
                      type="button"
                      onClick={() => requestCheckout(tier.id)}
                      className={`flex h-14 w-full items-center justify-center whitespace-nowrap rounded-xl px-4 font-semibold transition-all ${
                        tier.recommended
                          ? 'bg-primary text-white hover:brightness-110'
                          : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                      }`}
                    >
                      {t('pricing.passes.buyCta')}
                    </button>

                    <p className="mt-3 text-center text-sm text-slate-500">
                      {isAuthenticated
                        ? t('landing.pricing.passes.signedInPrompt')
                        : t('landing.pricing.passes.haveAccount')}{' '}
                      <Link
                        to={isAuthenticated ? '/dashboard' : '/login?redirect=/pricing'}
                        className="text-primary underline hover:no-underline"
                      >
                        {isAuthenticated
                          ? t('landing.pricing.passes.dashboardLink')
                          : t('landing.pricing.passes.loginLink')}
                      </Link>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative flex flex-col overflow-hidden rounded-[30px] border p-8 shadow-surface ${
                    plan.popular
                      ? 'border-secondary/60 bg-white ring-2 ring-secondary/20'
                      : 'border-primary/10 bg-white/90'
                  }`}
                >
                  {plan.popular && (
                    <span className="rounded-full bg-secondary px-3 py-1 text-center text-xs font-semibold uppercase tracking-wide text-white">
                      {t('pricing.mostChosen')}
                    </span>
                  )}

                  <h2 className="mt-4 text-2xl font-bold text-ink">
                    {getPlanLabel(plan.id, t)}
                  </h2>
                  <div className="mb-6 mt-4">
                    <span className="text-4xl font-bold text-primary">{formatCurrency(plan.price, i18n.language)}</span>
                    <span className="ml-1 text-sm text-slate-600">{t('pricing.perMonth')}</span>
                  </div>

                  <ul className="mb-8 space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-sm text-slate-700">
                        <svg
                          className="mr-2 mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={subscribing === plan.id}
                    className={`mt-auto flex h-14 w-full items-center justify-center whitespace-nowrap rounded-xl px-4 font-semibold transition-all ${
                      plan.popular
                        ? 'bg-primary text-white hover:brightness-110'
                        : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                    } disabled:opacity-50`}
                  >
                    {subscribing === plan.id ? t('dashboard.subscriptionCard.processing') : t('dashboard.subscriptionCard.choosePlan')}
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && !isDialogOpen && (
            <p className="mt-6 rounded-2xl border border-error/25 bg-error/10 p-4 text-center text-sm text-error">
              {error}
            </p>
          )}
        </div>
      </section>
      <Footer />

      <PassCheckoutDialog
        open={isDialogOpen}
        submitting={isSubmitting}
        error={error}
        onSubmit={submitEmail}
        onClose={closeDialog}
      />
    </div>
  );
};

export default PricingPage;
